/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * PwaInstallPrompt — Full PWA experience
 * Feature #53: Install prompt, offline indicator, update banner, offline data caching
 */
import React, { useState, useEffect, useCallback } from 'react';

const GOLD = '#D4A843';

const PwaInstallPrompt = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(window.matchMedia?.('(display-mode: standalone)').matches);
  const [swUpdate, setSwUpdate] = useState(false);
  const [swReg, setSwReg] = useState(null);
  const [offlineBanner, setOfflineBanner] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);

  useEffect(() => {
    // ── Online / Offline ──
    const goOnline = async () => {
      setIsOnline(true);
      // Replay queued mutations
      try {
        const { replayPendingMutations } = await import('@utils/offlineSync');
        await replayPendingMutations();
      } catch {}
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // ── Install prompt ──
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    // ── Register SW & detect updates ──
    if ('serviceWorker' in navigator && location.hostname !== 'localhost') {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        setSwReg(reg);
        reg.addEventListener('updatefound', () => {
          const worker = reg.installing;
          if (worker) {
            worker.addEventListener('statechange', () => {
              if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                setSwUpdate(true);
              }
            });
          }
        });

        // Hand the worker its runtime config. It has no import.meta.env, so it
        // cannot know the API origin on its own, and no localStorage, so the
        // origin is also persisted to IndexedDB for a push/sync that wakes it
        // with no tab open. Then warm the offline read cache.
        // (The token key here used to read 'token', which this app has never
        // written — so this whole block was a no-op.)
        import('@utils/offlineSync').then(({ getApiOrigin, setSyncMeta }) => {
          const apiOrigin = getApiOrigin();
          if (apiOrigin) setSyncMeta('apiOrigin', apiOrigin);
          const token = localStorage.getItem('accessToken');
          navigator.serviceWorker.ready.then((active) => {
            const worker = active.active || navigator.serviceWorker.controller;
            if (!worker) return;
            if (apiOrigin) worker.postMessage({ type: 'SET_CONFIG', apiOrigin });
            if (token) worker.postMessage({ type: 'CACHE_API_DATA', token });
          }).catch(() => {});
        }).catch(() => {});
      }).catch(() => {});
    }

    // Check pending sync count
    import('@utils/offlineSync').then(({ getPendingCount }) => getPendingCount().then(setPendingSync)).catch(() => {});

    // Show install prompt after delay (not already dismissed recently)
    const dismissedAt = parseInt(localStorage.getItem('vowsera-install-dismissed') || '0');
    if (Date.now() - dismissedAt > 7 * 86400000) {
      const timer = setTimeout(() => setShowInstall(true), 30000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('online', goOnline);
        window.removeEventListener('offline', goOffline);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  // ── Offline banner logic ──
  useEffect(() => {
    if (!isOnline) {
      setOfflineBanner(true);
      setWasOffline(true);
    } else if (wasOffline) {
      setOfflineBanner(true);
      const t = setTimeout(() => { setOfflineBanner(false); setWasOffline(false); }, 3000);
      return () => clearTimeout(t);
    } else {
      setOfflineBanner(false);
    }
  }, [isOnline, wasOffline]);

  // Refresh pending count periodically
  useEffect(() => {
    const iv = setInterval(() => {
      import('@utils/offlineSync').then(({ getPendingCount }) => getPendingCount().then(setPendingSync)).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setShowInstall(false);
  };

  const dismissInstall = () => {
    setShowInstall(false);
    localStorage.setItem('vowsera-install-dismissed', Date.now().toString());
  };

  const applyUpdate = () => {
    if (swReg?.waiting) {
      swReg.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return (
    <>
      {/* ═══ OFFLINE BANNER ═══ */}
      {offlineBanner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          padding: '8px 16px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600,
          background: isOnline ? '#22C55E' : '#EF4444', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          animation: 'pwa-slideDown 0.3s ease-out'
        }}>
          {isOnline ? (
            <><span>✅</span> Back online — syncing your changes...</>
          ) : (
            <><span>📡</span> You're offline — showing cached data
              {pendingSync > 0 && <span style={{ marginLeft: 8, background: 'rgba(0,0,0,0.2)', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem' }}>
                {pendingSync} pending {pendingSync === 1 ? 'change' : 'changes'}
              </span>}
            </>
          )}
        </div>
      )}

      {/* ═══ INSTALL PROMPT ═══ */}
      {showInstall && installPrompt && !isInstalled && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, width: '92%', maxWidth: 420,
          background: 'linear-gradient(135deg, #1A1040 0%, #2D1B69 100%)',
          borderRadius: 16, padding: '16px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: 14, color: '#fff',
          animation: 'pwa-slideUp 0.4s ease-out'
        }}>
          <div style={{ fontSize: '2rem', flexShrink: 0 }}>💍</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 2 }}>Install VowsEra</div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
              Access plans offline, get notifications, and enjoy app-like speed.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button onClick={handleInstall} style={{
              padding: '8px 16px', background: GOLD, color: '#1A1040',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer'
            }}>Install</button>
            <button onClick={dismissInstall} style={{
              padding: '4px 8px', background: 'transparent', color: 'rgba(255,255,255,0.5)',
              border: 'none', fontSize: '0.7rem', cursor: 'pointer'
            }}>Not now</button>
          </div>
        </div>
      )}

      {/* ═══ SW UPDATE BANNER ═══ */}
      {swUpdate && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9998,
          background: '#1E3A5F', color: '#fff', borderRadius: 12,
          padding: '12px 18px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem',
          animation: 'pwa-slideUp 0.4s ease-out'
        }}>
          <span style={{ fontSize: '1.2rem' }}>🔄</span>
          <div>
            <div style={{ fontWeight: 700 }}>Update Available</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>A new version is ready.</div>
          </div>
          <button onClick={applyUpdate} style={{
            padding: '6px 14px', background: GOLD, color: '#1A1040',
            border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer'
          }}>Update</button>
        </div>
      )}

      {/* Shared animations */}
      <style>{`
        @keyframes pwa-slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        @keyframes pwa-slideUp { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
};

export default PwaInstallPrompt;
